<script setup>
import { computed } from 'vue'
import { useCurrency } from '@/composables/useCurrency'

const props = defineProps({
  delivery:     { type: Object,  required: true },
  canCancel:    { type: Boolean, default: true },
  canEdit:      { type: Boolean, default: false },
  canReassign:  { type: Boolean, default: false },
})
const emit = defineEmits(['cancel', 'edit', 'reassign', 'live-map'])

const { formatDateTime, formatMoney, formatDate } = useCurrency()

function statusColor(status) {
  return {
    pending:     'pill--warn',
    assigned:    'pill--accent',
    in_progress: 'pill--accent',
    completed:   'pill--ok',
    cancelled:   'pill--muted'
  }[status] || 'pill--muted'
}

const CANCELLABLE = ['pending', 'assigned', 'in_progress']
const isActive = computed(() => CANCELLABLE.includes(props.delivery.status))
</script>

<template>
  <div class="card">
    <div class="card__header">
      <h3>{{ delivery.kind === 'handover' ? 'Handover ↗' : 'Pickup ↙' }}</h3>
      <div class="flex items-center gap-2">
        <span
          v-if="delivery.fee_cents"
          class="fw-value tabular-nums text-sm"
          style="color: var(--inkSoft);"
        >{{ formatMoney(delivery.fee_cents) }}</span>
        <span
          v-if="delivery.paid_at"
          class="pill pill--ok"
          :title="formatDate(delivery.paid_at)"
        >Paid</span>
        <span
          v-else-if="delivery.fee_cents > 0"
          class="pill pill--unpaid"
        >Unpaid</span>
        <span :class="['pill', statusColor(delivery.status)]">{{ delivery.status }}</span>
      </div>
    </div>

    <dl class="kv">
      <dt>Address</dt>
      <dd>{{ delivery.address }}</dd>
      <dt>Scheduled</dt>
      <dd>{{ delivery.scheduled_for ? formatDateTime(delivery.scheduled_for) : '—' }}</dd>
      <template v-if="delivery.driver">
        <dt>Driver</dt>
        <dd>{{ delivery.driver.name }}{{ delivery.driver.phone ? ` (${delivery.driver.phone})` : '' }}</dd>
      </template>
      <template v-if="delivery.notes">
        <dt>Note</dt>
        <dd>{{ delivery.notes }}</dd>
      </template>
      <template v-if="delivery.cancelled_reason">
        <dt>Cancel reason</dt>
        <dd>{{ delivery.cancelled_reason }}</dd>
      </template>
    </dl>

    <div
      v-if="delivery.proof_photos && delivery.proof_photos.length"
      class="proof"
    >
      <div class="proof__heading">
        Proof of handover
      </div>
      <div class="proof__grid">
        <a
          v-for="photo in delivery.proof_photos"
          :key="photo.id"
          :href="photo.url"
          target="_blank"
          rel="noopener"
          class="proof__thumb"
        >
          <img
            :src="photo.url"
            alt=""
          >
        </a>
      </div>
    </div>

    <div
      v-if="isActive && (canEdit || canReassign || canCancel)"
      class="card__actions"
    >
      <button
        v-if="canEdit"
        class="action"
        @click="emit('edit', delivery.id)"
      >
        Edit
      </button>
      <button
        v-if="canReassign"
        class="action"
        @click="emit('reassign', delivery.id)"
      >
        Reassign
      </button>
      <button
        v-if="isActive"
        class="action"
        @click="emit('live-map', delivery.id)"
      >
        Live map
      </button>
      <button
        v-if="canCancel"
        class="action action--danger"
        @click="emit('cancel', delivery.id)"
      >
        Cancel
      </button>
    </div>
  </div>
</template>

<style scoped>
.card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 0.75rem;
  padding: 1rem 1.25rem;
  margin-bottom: 0.75rem;
}
.card__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}
.card__header h3 {
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  color: var(--ink);
}
.kv {
  display: grid;
  grid-template-columns: 7rem 1fr;
  gap: 0.3rem 1rem;
  margin: 0;
  font-size: 0.875rem;
}
.kv dt { color: var(--muted); }
.kv dd { color: var(--ink); }
.pill {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.75rem;
}
.pill--warn   { background: color-mix(in oklch, var(--warn)   14%, var(--surface)); color: var(--warn); }
.pill--accent { background: color-mix(in oklch, var(--accent) 14%, var(--surface)); color: var(--accent); }
.pill--ok     { background: color-mix(in oklch, var(--ok)     14%, var(--surface)); color: var(--ok); }
.pill--muted  { background: color-mix(in oklch, var(--muted)  12%, var(--surface)); color: var(--muted); }
.pill--unpaid { background: color-mix(in oklch, var(--surfaceAlt) 100%, var(--surface)); color: var(--muted); }
.card__actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
  justify-content: flex-end;
}
.action {
  padding: 0.25rem 0.75rem;
  background: var(--surfaceAlt);
  color: var(--ink);
  border: 1px solid var(--line);
  border-radius: var(--radius-button, 8px);
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: 500;
}
.action:hover { background: var(--surface); }
.action--danger {
  color: var(--danger);
  border-color: color-mix(in oklch, var(--danger) 40%, var(--line));
  background: transparent;
}
.action--danger:hover { background: color-mix(in oklch, var(--danger) 8%, var(--surface)); }
.proof {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px dashed var(--line);
}
.proof__heading {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--muted);
  margin-bottom: 0.4rem;
}
.proof__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(4rem, 1fr));
  gap: 0.4rem;
}
.proof__thumb {
  display: block;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  border-radius: 0.4rem;
  background: var(--surfaceAlt);
  border: 1px solid var(--line);
}
.proof__thumb img { width: 100%; height: 100%; object-fit: cover; }
</style>
